/**
 * Utilidad para hacer peticiones HTTP con reintentos automáticos.
 * Maneja errores 503 (cold start de Cloud Run) reintentando la petición.
 */

const INTENTOS_MAXIMOS = 3;
const RETRASO_BASE_MS = 1000;

/**
 * Ejecuta una petición fetch con reintentos automáticos para errores de red y 503.
 */
export async function fetchConReintentos(
    url: string,
    opciones: RequestInit = {},
    intentosMaximos: number = INTENTOS_MAXIMOS
): Promise<Response> {
    let ultimoError: unknown;

    for (let intento = 0; intento < intentosMaximos; intento++) {
        try {
            const response = await fetch(url, opciones);

            // Si es 503 (Service Unavailable), reintentar
            if (response.status === 503 && intento < intentosMaximos - 1) {
                console.warn(`[fetchConReintentos] Error 503, reintentando... (${intento + 1}/${intentosMaximos})`);
                await esperar(RETRASO_BASE_MS * (intento + 1));
                continue;
            }

            // El 401 se devuelve tal cual, a propósito.
            //
            // Antes esta función cerraba la sesión y redirigía al login por su
            // cuenta. Eso mezclaba transporte con política de autenticación y,
            // peor, hacía imposible que quien llama decidiera otra cosa: la
            // redirección ocurría antes de que pudiera mirar la respuesta.
            // Ahora esa decisión vive en `services/clienteApi.ts`, en un único
            // sitio, que es también donde entrará la renovación de token.
            return response;
        } catch (error) {
            ultimoError = error;

            // Solo reintentar en errores de red (no en errores de parsing, etc.)
            const esErrorDeRed = error instanceof TypeError && error.message.includes('fetch');

            if (esErrorDeRed && intento < intentosMaximos - 1) {
                console.warn(`[fetchConReintentos] Error de red, reintentando... (${intento + 1}/${intentosMaximos})`, error instanceof Error ? error.message : String(error));
                await esperar(RETRASO_BASE_MS * (intento + 1));
                continue;
            }

            throw error;
        }
    }

    throw ultimoError;
}

/**
 * Espera un número de milisegundos.
 */
function esperar(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Wrapper para hacer peticiones GET con reintentos.
 */
export async function getConReintentos(url: string, token?: string): Promise<Response> {
    const opciones: RequestInit = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
        }
    };
    return fetchConReintentos(url, opciones);
}

/**
 * Wrapper para hacer peticiones POST con reintentos.
 */
export async function postConReintentos(url: string, body: any, token?: string): Promise<Response> {
    const opciones: RequestInit = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(body)
    };
    return fetchConReintentos(url, opciones);
}
